/**
 * Connection Diagnostics — error code → human message map.
 *
 * Maps both SQL-level error numbers (e.g. 4060) and driver-level codes
 * (e.g. `ELOGIN`) to short, neutral, human-friendly descriptions surfaced
 * by `ConnectionDiagnosticsDialog`.
 *
 * Per `Ralph/ConnectionDiagnostics/PRD.md` → Slice 3:
 *   297 / 916 → permission to access this database is missing on the server
 *   4060      → database doesn't exist or no permission to open it
 *   18456     → server rejected the login (no server-level mapping)
 *   40531     → same as 18456 (Azure SQL phrasing)
 *   40615     → Azure SQL firewall blocked this client IP
 *   default   → pass through the raw error message verbatim
 *
 * Driver codes:
 *   ELOGIN    → mapped to the same description as 4060 / 18456 when the
 *               numeric code is missing (Azure SQL Edge / classic mssql
 *               driver sometimes reports `ELOGIN` without populating
 *               `info.number` for "Cannot open database").
 *   ETIMEOUT  → handled by the dialog separately via `timedOut: true`.
 */

export type DiagnosticsErrorMessage = {
  /** Short human-friendly description. */
  description: string;
  /** Stable category — useful for analytics / styling. */
  category: 'permission' | 'login' | 'database_missing' | 'firewall' | 'other';
};

const SQL_ERROR_MAP: Record<number, DiagnosticsErrorMessage> = {
  297: {
    description: "Your account doesn't have permission to access this database on the server.",
    category: 'permission',
  },
  916: {
    description: "Your account doesn't have permission to access this database on the server.",
    category: 'permission',
  },
  4060: {
    description: "The database doesn't exist or you don't have permission to open it.",
    category: 'database_missing',
  },
  18456: {
    description: 'SQL Server rejected the login (no server-level mapping for this principal).',
    category: 'login',
  },
  40531: {
    description: 'SQL Server rejected the login (no server-level mapping for this principal).',
    category: 'login',
  },
  40615: {
    description: 'The Azure SQL firewall blocked this client IP.',
    category: 'firewall',
  },
};

const DRIVER_CODE_MAP: Record<string, DiagnosticsErrorMessage> = {
  ELOGIN: {
    description: 'SQL Server rejected the login (no server-level mapping for this principal).',
    category: 'login',
  },
};

export type ErrorLookupArgs = {
  /** SQL-level numeric error code (e.g. 4060). */
  sqlErrorCode?: number;
  /** Driver-level string code (e.g. `ELOGIN`). */
  code?: string;
  /** Raw fallback message used when no mapping matches. */
  fallbackMessage?: string;
};

/**
 * Look up a human-friendly description for an error.
 *
 * Numeric `sqlErrorCode` wins when present. If absent, the driver `code` is
 * consulted. If neither maps, the function returns `fallbackMessage` (or a
 * generic placeholder) under category `'other'`.
 */
export function lookupConnectionDiagnosticsError(
  args: ErrorLookupArgs
): DiagnosticsErrorMessage {
  if (typeof args.sqlErrorCode === 'number') {
    const mapped = SQL_ERROR_MAP[args.sqlErrorCode];
    if (mapped) return mapped;
  }
  if (args.code && DRIVER_CODE_MAP[args.code]) {
    return DRIVER_CODE_MAP[args.code];
  }
  return {
    description: args.fallbackMessage?.trim() || 'The server returned an error.',
    category: 'other',
  };
}
